import { buildLayout } from '../domain/stitches';
import { EMPTY } from '../domain/types';
import type { Pattern } from '../domain/types';
import { parsePatternFile, serializePattern } from './format';

const INDEX_KEY = 'beading-designer:index';
const PROJECT_KEY = (id: string) => `beading-designer:project:${id}`;
const PREFS_KEY = 'beading-designer:prefs';

export interface ProjectSummary {
  id: string;
  name: string;
  modified: number;
  width: number;
  height: number;
  stitch: string;
  thumb: string;
}

function readIndex(): ProjectSummary[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(list: ProjectSummary[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(list));
}

export function listProjects(): ProjectSummary[] {
  return readIndex().sort((a, b) => b.modified - a.modified);
}

/** Small preview image for the project list. */
export function makeThumbnail(pattern: Pattern, maxPx = 96): string {
  const layout = buildLayout(pattern.stitch, pattern.beadSize, pattern.width, pattern.height);
  const scale = Math.min(maxPx / layout.totalW, maxPx / layout.totalH);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(layout.totalW * scale));
  canvas.height = Math.max(1, Math.round(layout.totalH * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  for (let row = 0; row < pattern.height; row++) {
    for (let col = 0; col < pattern.width; col++) {
      const v = pattern.cells[row * pattern.width + col];
      if (v === EMPTY) continue;
      const p = layout.pos(col, row);
      ctx.fillStyle = pattern.palette[v]?.hex ?? '#888';
      ctx.fillRect(
        p.x * scale,
        p.y * scale,
        Math.max(1, layout.cellW * scale),
        Math.max(1, layout.cellH * scale),
      );
    }
  }
  return canvas.toDataURL('image/png');
}

export class StorageFullError extends Error {}

export function saveProject(pattern: Pattern): ProjectSummary {
  const summary: ProjectSummary = {
    id: pattern.id,
    name: pattern.name,
    modified: Date.now(),
    width: pattern.width,
    height: pattern.height,
    stitch: pattern.stitch,
    thumb: makeThumbnail(pattern),
  };
  try {
    localStorage.setItem(PROJECT_KEY(pattern.id), serializePattern(pattern));
    const list = readIndex().filter((p) => p.id !== pattern.id);
    list.push(summary);
    writeIndex(list);
  } catch (err) {
    throw new StorageFullError(
      'Browser storage is full. Delete some saved patterns, or export this one to a file.',
      { cause: err },
    );
  }
  return summary;
}

export function loadProject(id: string): Pattern | null {
  const raw = localStorage.getItem(PROJECT_KEY(id));
  if (!raw) return null;
  try {
    const result = parsePatternFile(raw, `${id}.bead.json`);
    result.pattern.id = id;
    return result.pattern;
  } catch {
    return null;
  }
}

export function deleteProject(id: string): void {
  localStorage.removeItem(PROJECT_KEY(id));
  writeIndex(readIndex().filter((p) => p.id !== id));
}

export function renameProject(id: string, name: string): void {
  const pattern = loadProject(id);
  if (!pattern) return;
  pattern.name = name;
  saveProject(pattern);
}

export interface Prefs {
  libraryId: string;
  theme: 'dark' | 'light';
  lastProjectId: string | null;
  autosave: boolean;
}

export function loadPrefs(): Prefs {
  const fallback: Prefs = { libraryId: 'delica11', theme: 'light', lastProjectId: null, autosave: true };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

export function savePrefs(prefs: Partial<Prefs>): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...prefs }));
  } catch {
    // Preferences are not worth surfacing an error for.
  }
}
